/**
 * Per-user preferences for every mount, moved from the shell's `services/preferences-service.ts`
 * and `services/preference-namespace.ts` (kernel apps, WS-14 section 3.6; layered shell surfaces,
 * WS-8 section 3.5 and 3.6, R-22).
 *
 * One store per document, keyed by namespace, reads `GET /v1/auth/preferences/:ns` and sends
 * debounced partial `PATCH`es through the origin's `platformFetch`. core-api shallow-merges a
 * `PATCH` into the stored row, so a partial write never removes a sibling key. Both host documents
 * build one: the shell on `<dp>` (WS-16) and the kernel SPA on `kernel.<dp>` (WS-15), so the admin
 * app's recents entry reaches the row that `<dp>` home reads.
 */
import type { SurfacePreferencesService } from '@zion/sdk-surfaces/v1';
import { createCoreApiRequest, SERVICES_FETCH_TIMEOUT_MS, type BearerIdentity } from './fetch-policy.js';
import {
  SurfaceServiceRefusedError,
  isPlainJsonObject,
  type MountScope,
  type ServiceClock,
  type ServiceSession,
  type ServiceSessionSnapshot,
  type SurfaceServiceRefusalReason,
} from './kit.js';
import { isRoutedRoot } from './polarity.js';

/**
 * The namespace grammar: a letter, digit or `_`, then up to 127 of letters, digits, `_`, `.`,
 * `:` and `-`. At most 128 characters. `..` alone fails the first character. A namespace becomes
 * the `:fixtureId` segment of `/v1/auth/preferences/:fixtureId`, so the grammar admits no `/`,
 * `%`, `?`, `#` or whitespace.
 */
export const PREFERENCE_NAMESPACE_PATTERN = '^[A-Za-z0-9_][A-Za-z0-9_.:-]{0,127}$';

/** The recents log's namespace. Its one writer is the host's recents controller (`recents.ts`). */
export const RECENTS_NAMESPACE = 'home-recents';

/**
 * The namespaces the routed root may use besides its own: the stored home and sidebar
 * preferences, so they survive the move into `default-shell`. Frozen.
 */
export const ROOT_LEGACY_NAMESPACES: readonly string[] = Object.freeze(['home', 'shell']);

const NAMESPACE_RE = new RegExp(PREFERENCE_NAMESPACE_PATTERN);

/**
 * Judge one namespace for one mount (security branch S5), in this order:
 *
 * 1. A non-string, or a string outside {@link PREFERENCE_NAMESPACE_PATTERN}: `'namespace-invalid'`.
 * 2. {@link RECENTS_NAMESPACE}: `'namespace-reserved'`, for every mount.
 * 3. The mount's `appId`, alone or followed by `:` and a suffix: `null` (allowed). A system
 *    mount's `appId` is `system`.
 * 4. A routed root mount and a namespace in {@link ROOT_LEGACY_NAMESPACES}: `null`.
 * 5. Anything else: `'namespace-foreign'`.
 *
 * @param namespace The value a surface passed; any type reaches here from untyped code.
 * @param mount The mount's registry slug and whether it is the routed root.
 * @returns `null` when allowed, else the refusal reason. Pure; never throws.
 */
export function checkPreferenceNamespace(
  namespace: unknown,
  mount: { readonly appId: string; readonly root: boolean },
): SurfaceServiceRefusalReason | null {
  if (typeof namespace !== 'string' || !NAMESPACE_RE.test(namespace)) return 'namespace-invalid';
  if (namespace === RECENTS_NAMESPACE) return 'namespace-reserved';
  if (namespace === mount.appId || namespace.startsWith(`${mount.appId}:`)) return null;
  if (mount.root && ROOT_LEGACY_NAMESPACES.includes(namespace)) return null;
  return 'namespace-foreign';
}

/** How long a write waits for the next one before the store sends it, in milliseconds. */
export const PREFERENCES_DEBOUNCE_MS = 500;

/** A namespace's value: a frozen copy of the stored row with the unsent writes merged over it. */
export type PreferenceValue = Readonly<Record<string, unknown>>;

/**
 * The unsent partial of one namespace. Its object identity names the batch: `send` hands the same
 * object to the batch's `PATCH`, so a failed `PATCH` finds the keys it carried.
 */
interface PendingPartial {
  keys: Record<string, unknown>;
}

/**
 * The keys written while a `GET` of one namespace is out, per key in write order, each with the
 * batch that carries it. The last writer that is left gives the key's value.
 */
type WrittenKeys = Map<string, { readonly value: unknown; readonly batch: PendingPartial }[]>;

/**
 * The document-wide preferences store.
 *
 * Its own `read` and `write` skip the namespace check; only `recents.ts` calls them, for
 * `home-recents`. Every mount goes through `view`.
 *
 * - `read(ns)` waits while the session hydrates. Anonymous, it resolves `null` ("could not
 *   look") and sends nothing. Authenticated, it sends one `GET` per namespace, shared by
 *   concurrent readers and cached on success. A rejected `GET`, or a body that is not a JSON
 *   object, resolves `null` and caches nothing, so the next read sends a new `GET`. The value
 *   carries the unsent partial merged over the row. It never rejects.
 * - The `GET` of a namespace leaves only after every `PATCH` of that namespace in flight at the
 *   `GET`'s start settles: answered, failed, timed out or aborted (R-81 b). A `PATCH` sent while
 *   the `GET` waits or is out is not in that wait; the merge below carries its keys. A failed
 *   `PATCH` does not fail the `read`. The wait is as long as that `PATCH`, the policy's cold-start
 *   and maintenance retries included.
 * - The row a `GET` caches is its body with two sets of keys merged over it in write order: the
 *   unsent partial at the `GET`'s start, and every `write` made before it answers, sent or not.
 *   So every later `read` in the document shows the written keys, and a cached row never lacks
 *   one (S2 WS-8 architecture-9, rejudge-5).
 * - One rule takes keys out of that merge. When a `PATCH` fails or times out before the `GET`
 *   answers, the values that `PATCH` carried leave the merge. A key that another `write` also set
 *   keeps the value of the last such `write` whose `PATCH` has not failed. A key with no such
 *   `write` shows the body's value, or is absent when the body has none: the body is the server's
 *   row. A `PATCH` that times out at the client can still land at core-api after the `GET` read
 *   the row; the cached row then lacks its keys until the document loads again (E-1, R-87).
 * - `write(ns, partial)` throws `value-invalid` for a partial that is not a plain JSON object.
 *   Without an authenticated session it drops the write. Otherwise it merges the keys into the
 *   namespace's pending partial and re-arms a {@link PREFERENCES_DEBOUNCE_MS} timer; on fire it
 *   sends `PATCH` with only the keys written since the last send. A failed `PATCH` is swallowed.
 * - `flush(ns?)` sends the pending partial of `ns`, or of every namespace, now; it resolves once
 *   those `PATCH`es settle and never rejects.
 * - Every `GET` and `PATCH` goes through `createCoreApiRequest` over the policy's `platformFetch`.
 *   The policy bounds the wait for the headers of each attempt, and the body read after the
 *   headers has the `timeoutMs` bound. An attempt the policy times out and a body that stalls past
 *   the bound are each a rejected call: a `read` resolves `null` and frees the namespace for the
 *   next `GET`, and a `flush` resolves.
 * - A change of `status`, `userId` or `orgId` drops every pending partial and timer, clears the
 *   cache and makes every `GET` in flight write nothing and resolve `null`. It also aborts every
 *   `GET` and `PATCH` of the old identity through the request signal, so a retry inside the
 *   policy sends no further attempt. A `read` that waits on a `PATCH` of the old identity resolves
 *   `null` and sends no `GET`. A `GET` of the new identity waits on no `PATCH` of the old one and
 *   carries none of its keys. A token refresh alone changes nothing.
 * - Before each network attempt, the store reads `liveIdentity()`, the identity of the bearer the
 *   policy attaches now (security branch S8). When it is not `null` and its `sub` or `orgId`
 *   differs from the session's `userId` or `orgId`, the attempt rejects with an `AbortError` and
 *   sends nothing. This holds while the session port has not yet reported the change, so no write
 *   crosses identities (S2 WS-8 architecture-6, security-6).
 * - `view(scope)` runs {@link checkPreferenceNamespace} first: `read` rejects and `write` throws
 *   with its reason. Its `flush()` sends the namespaces this mount wrote. The mount's end flushes
 *   them, fire-and-forget. After the mount ends, `read` and `flush` reject and `write` throws
 *   `disposed`.
 * - `dispose()` flushes everything, then stops listening to the session; a `read` still waiting
 *   for hydration, or started later, resolves `null`.
 */
export interface PreferencesStore {
  read(namespace: string): Promise<PreferenceValue | null>;
  write(namespace: string, partial: Readonly<Record<string, unknown>>): void;
  flush(namespace?: string): Promise<void>;
  view(scope: MountScope): SurfacePreferencesService;
  dispose(): void;
}

function identityOf(snapshot: ServiceSessionSnapshot): string {
  return `${snapshot.status}\n${snapshot.userId ?? ''}\n${snapshot.orgId ?? ''}`;
}

/**
 * The bearer check of one network attempt, against the session the store sees now.
 *
 * @returns `null` when the policy attaches no bearer (an attempt with no bearer writes to no row),
 *   or when the bearer's `sub` and `orgId` equal the snapshot's `userId` and `orgId`. Otherwise an
 *   aborted signal with an `AbortError` reason, so the attempt sends nothing.
 *
 * The store compares with the live snapshot, never with one captured at the request's start: a
 * change the session port reports also aborts the request's identity signal, so while that
 * signal is live the two snapshots are the same identity.
 */
function bearerCheck(identity: BearerIdentity | null, snapshot: ServiceSessionSnapshot): AbortSignal | null {
  if (identity === null) return null;
  if (identity.sub === snapshot.userId && identity.orgId === snapshot.orgId) return null;
  return AbortSignal.abort(new DOMException('the live bearer belongs to another identity', 'AbortError'));
}

/**
 * Build the store.
 *
 * @param session This origin's session port.
 * @param clock The debounce clock; {@link ServiceClock} `systemClock` in production.
 * @param platformFetch The policy's `platformFetch` (WS-14 section 3.6, change 3). It attaches the
 *   live session bearer and owns the 401, cold-start and maintenance paths.
 * @param liveIdentity The policy's `liveIdentity`, read before each network attempt.
 * @param timeoutMs The body bound of each call, {@link SERVICES_FETCH_TIMEOUT_MS} in production.
 *   A value out of range rejects each call with a `RangeError` and sends nothing, so a `read`
 *   resolves `null` and a `flush` resolves.
 */
export function createPreferencesStore(
  session: ServiceSession,
  clock: ServiceClock,
  platformFetch: typeof fetch,
  liveIdentity: () => BearerIdentity | null,
  timeoutMs: number = SERVICES_FETCH_TIMEOUT_MS,
): PreferencesStore {
  const coreApi = createCoreApiRequest(platformFetch, timeoutMs, () => bearerCheck(liveIdentity(), session.current()));
  // One controller per identity generation. `resetIdentity` aborts it, so no attempt of the old
  // identity leaves after the session port reports the change.
  let identityAbort = new AbortController();
  // `PREFERENCE_NAMESPACE_PATTERN` admits no `/`, `%`, `?` or `#`, so a namespace is one path
  // segment as it is. A failed or aborted `GET` or `PATCH` rejects; the callers below read that
  // as `null` or swallow it.
  const getRow = (namespace: string): Promise<Record<string, unknown>> =>
    coreApi<Record<string, unknown>>(`/v1/auth/preferences/${namespace}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: identityAbort.signal,
    });
  const patchRow = (namespace: string, partial: Record<string, unknown>): Promise<Record<string, unknown>> =>
    coreApi<Record<string, unknown>>(`/v1/auth/preferences/${namespace}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(partial),
      signal: identityAbort.signal,
    });
  const cache = new Map<string, Readonly<Record<string, unknown>>>();
  const inFlight = new Map<string, Promise<Readonly<Record<string, unknown>> | null>>();
  const pending = new Map<string, PendingPartial>();
  // The keys merged over the body of the namespace's first `GET`: the unsent partial at its start,
  // then every write until it answers. `send` clears `pending`, and the `GET` can answer with a
  // row read before that `PATCH` landed, so `fetchRow` merges these over the body it caches
  // (S2 WS-8 architecture-9, rejudge-5). A `PATCH` that fails takes its batch's values out (E-1).
  const writtenDuringFetch = new Map<string, WrittenKeys>();
  // The `PATCH`es in flight per namespace, as settle promises that never reject. `fetchRow` waits
  // on them before its `GET` leaves (R-81 b).
  let patchesInFlight = new Map<string, Set<Promise<void>>>();
  const timers = new Map<string, number>();
  let hydrationWaiters: (() => void)[] = [];
  let identity = identityOf(session.current());
  let generation = 0;
  let stopped = false;

  const resetIdentity = (): void => {
    for (const handle of timers.values()) clock.clearTimeout(handle);
    timers.clear();
    pending.clear();
    cache.clear();
    inFlight.clear();
    writtenDuringFetch.clear();
    // A fresh map, so a `PATCH` of the old identity that settles later removes itself from its own
    // map and never from the new identity's.
    patchesInFlight = new Map();
    generation += 1;
    identityAbort.abort(new DOMException('the session identity changed', 'AbortError'));
    identityAbort = new AbortController();
  };

  const stopSession = session.subscribe(() => {
    const next = identityOf(session.current());
    if (next !== identity) {
      identity = next;
      resetIdentity();
    }
    // Every waiting read re-checks the status itself and waits again while it still hydrates.
    const waiters = hydrationWaiters;
    hydrationWaiters = [];
    for (const wake of waiters) wake();
  });

  const withPending = (namespace: string, row: Readonly<Record<string, unknown>>): PreferenceValue =>
    Object.freeze({ ...row, ...pending.get(namespace)?.keys });

  const addWritten = (written: WrittenKeys, keys: Readonly<Record<string, unknown>>, batch: PendingPartial): void => {
    for (const [key, value] of Object.entries(keys)) {
      const writers = written.get(key) ?? [];
      writers.push({ value, batch });
      written.set(key, writers);
    }
  };

  // A `PATCH` that failed or timed out takes the values its batch carried out of the namespace's
  // merge, so the body gives those keys (E-1, R-87). A `PATCH` of an old identity finds no entry of
  // its batch in the new identity's map.
  const dropWritten = (namespace: string, batch: PendingPartial): void => {
    const written = writtenDuringFetch.get(namespace);
    if (written === undefined) return;
    for (const [key, writers] of written) {
      const kept = writers.filter((writer) => writer.batch !== batch);
      if (kept.length === 0) written.delete(key);
      else written.set(key, kept);
    }
  };

  // `Object.fromEntries` defines each key as an own property, `__proto__` included, and a later
  // entry of a key replaces an earlier one, so the last writer gives the value.
  const writtenRecord = (written: WrittenKeys): Record<string, unknown> =>
    Object.fromEntries([...written].flatMap(([key, writers]) => writers.map((writer) => [key, writer.value])));

  const fetchRow = (namespace: string): Promise<Readonly<Record<string, unknown>> | null> => {
    const shared = inFlight.get(namespace);
    if (shared !== undefined) return shared;
    const captured = generation;
    // The unsent partial is written and not yet sent, so the body cannot carry it; it seeds the
    // keys merged over the body, and every `write` before the answer adds to them.
    const written: WrittenKeys = new Map();
    const unsent = pending.get(namespace);
    if (unsent !== undefined) addWritten(written, unsent.keys, unsent);
    writtenDuringFetch.set(namespace, written);
    const request = Promise.all(patchesInFlight.get(namespace) ?? [])
      .then(async (): Promise<Readonly<Record<string, unknown>> | null> => {
        if (captured !== generation) return null;
        const body = await getRow(namespace);
        if (captured !== generation || !isPlainJsonObject(body)) return null;
        const row = Object.freeze({ ...body, ...writtenRecord(written) });
        cache.set(namespace, row);
        return row;
      })
      .catch(() => null)
      .finally(() => {
        if (inFlight.get(namespace) !== request) return;
        inFlight.delete(namespace);
        writtenDuringFetch.delete(namespace);
      });
    inFlight.set(namespace, request);
    return request;
  };

  const read = async (namespace: string): Promise<PreferenceValue | null> => {
    while (!stopped && session.current().status === 'hydrating') {
      await new Promise<void>((wake) => hydrationWaiters.push(wake));
    }
    if (stopped || session.current().status !== 'authenticated') return null;
    const cached = cache.get(namespace);
    if (cached !== undefined) return withPending(namespace, cached);
    const captured = generation;
    const row = await fetchRow(namespace);
    if (row === null || captured !== generation) return null;
    return withPending(namespace, row);
  };

  const send = (namespace: string): Promise<void> => {
    const handle = timers.get(namespace);
    if (handle !== undefined) clock.clearTimeout(handle);
    timers.delete(namespace);
    const batch = pending.get(namespace);
    if (batch === undefined) return Promise.resolve();
    pending.delete(namespace);
    const settled = patchRow(namespace, batch.keys).then(
      () => undefined,
      () => {
        dropWritten(namespace, batch);
      },
    );
    const sending = patchesInFlight.get(namespace) ?? new Set<Promise<void>>();
    patchesInFlight.set(namespace, sending);
    sending.add(settled);
    return settled.then(() => {
      sending.delete(settled);
      if (sending.size === 0 && patchesInFlight.get(namespace) === sending) patchesInFlight.delete(namespace);
    });
  };

  const write = (namespace: string, partial: Readonly<Record<string, unknown>>): void => {
    if (!isPlainJsonObject(partial)) {
      throw new SurfaceServiceRefusedError('value-invalid', 'preferences.write takes a plain JSON object');
    }
    if (session.current().status !== 'authenticated') return;
    const batch = pending.get(namespace) ?? { keys: {} };
    batch.keys = { ...batch.keys, ...partial };
    pending.set(namespace, batch);
    const cached = cache.get(namespace);
    if (cached !== undefined) {
      cache.set(namespace, Object.freeze({ ...cached, ...partial }));
    } else {
      const written = writtenDuringFetch.get(namespace);
      if (written !== undefined) addWritten(written, partial, batch);
    }
    const handle = timers.get(namespace);
    if (handle !== undefined) clock.clearTimeout(handle);
    timers.set(
      namespace,
      clock.setTimeout(() => {
        timers.delete(namespace);
        void send(namespace);
      }, PREFERENCES_DEBOUNCE_MS),
    );
  };

  const flush = async (namespace?: string): Promise<void> => {
    const namespaces = namespace === undefined ? [...pending.keys()] : [namespace];
    await Promise.all(namespaces.map(send));
  };

  return {
    read,
    write,
    flush,
    view(scope) {
      const mount = { appId: scope.request.appId, root: isRoutedRoot(scope) };
      const written = new Set<string>();
      const refuse = (namespace: unknown, member: string): SurfaceServiceRefusedError | null => {
        const reason = checkPreferenceNamespace(namespace, mount);
        return reason === null ? null : new SurfaceServiceRefusedError(reason, `${member} namespace`);
      };
      scope.own(() => {
        for (const namespace of written) void send(namespace);
      });
      return scope.guard(
        {
          read: (namespace: string) => {
            const refusal = refuse(namespace, 'preferences.read');
            return refusal === null ? read(namespace) : Promise.reject(refusal);
          },
          write: (namespace: string, patch: Readonly<Record<string, unknown>>) => {
            const refusal = refuse(namespace, 'preferences.write');
            if (refusal !== null) throw refusal;
            write(namespace, patch);
            written.add(namespace);
          },
          flush: async () => {
            await Promise.all([...written].map(send));
          },
        },
        ['read', 'flush'],
      );
    },
    dispose() {
      void flush();
      stopped = true;
      stopSession();
      const waiters = hydrationWaiters;
      hydrationWaiters = [];
      for (const wake of waiters) wake();
    },
  };
}
